'use client';

import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';

interface VideoModalContextType {
  isOpen: boolean;
  videoUrl: string | null;
  videoAlt: string | null;
  objectPosition?: string;
  openModal: (url: string, alt?: string, objectPosition?: string) => void;
  closeModal: () => void;
}

const VideoModalContext = createContext<VideoModalContextType | undefined>(undefined);

export function VideoModalProvider({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [videoAlt, setVideoAlt] = useState<string | null>(null);
  const [objectPosition, setObjectPosition] = useState<string | undefined>(undefined);

  const openModal = useCallback((url: string, alt?: string, objectPosition?: string) => {
    setVideoUrl(url);
    setVideoAlt(alt || null);
    setObjectPosition(objectPosition);
    setIsOpen(true);
  }, []);

  const closeModal = useCallback(() => {
    setIsOpen(false);
    // Clear URL after a short delay to allow closing animation
    setTimeout(() => {
      setVideoUrl(null);
      setVideoAlt(null);
      setObjectPosition(undefined);
    }, 300);
  }, []);

  return (
    <VideoModalContext.Provider
      value={{
        isOpen,
        videoUrl,
        videoAlt,
        objectPosition,
        openModal,
        closeModal,
      }}
    >
      {children}
    </VideoModalContext.Provider>
  );
}

export function useVideoModal() {
  const context = useContext(VideoModalContext);
  if (context === undefined) {
    throw new Error('useVideoModal must be used within a VideoModalProvider');
  }
  return context;
}

